import { useEffect, useState } from "react";
import TextInput from "../../components/ui/TextInput";
import { LuTicketPercent } from "react-icons/lu";
import axios from "axios";
import { useAuthStore } from "../../context/useAuthStore";
import toast from "react-hot-toast";
import logo from "../../assets/llb360.png";
import secureLocalStorage from "react-secure-storage";
import { ROUTES } from "../../constant/routes";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID;

type Subject = {
  subscriptionId: string;
  semesterYear: string;
  semesterNumber: string;
  status: string;
  endDate: string;
  subjectName: string;
  price: number;
};

const CheckOutPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [cart, setCart] = useState<Subject[]>([]);
  const [errors, setErrors] = useState<{ couponCode: string }>({
    couponCode: "",
  });
  const [form, setForm] = useState<{ couponCode: string }>({ couponCode: "" });
  const [showCoupon, setShowCoupon] = useState(false);
  const [discount, setDiscount] = useState(0); // percentage
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const storedCart = secureLocalStorage.getItem("subjectCart");
      if (storedCart) {
        const parsed = JSON.parse(storedCart as string);
        const updated = parsed.map((item: Subject) => ({
          ...item,
          price: item.price ?? 500,
        }));
        setCart(updated);
        const subIds = updated
          .map((item: { subscriptionId: any }) => item.subscriptionId)
          .filter(Boolean);
        if (subIds.length > 0) {
          secureLocalStorage.setItem("subscriptionIds", JSON.stringify(subIds));
        }
      }
    } catch {
      setCart([]);
    }
  }, []);

  const removeFromCart = (subjectName: string) => {
    const updatedCart = cart.filter((item) => item.subjectName !== subjectName);
    setCart(updatedCart);
    secureLocalStorage.setItem("subjectCart", JSON.stringify(updatedCart));
    const updatedSubIds = updatedCart.map((item) => item.subscriptionId);
    secureLocalStorage.setItem(
      "subscriptionIds",
      JSON.stringify(updatedSubIds)
    );
    setAppliedCoupon(null);
    setDiscount(0);
    setForm({ couponCode: "" });
    toast.success("Subject removed from cart.");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const totalAmount = cart.reduce((acc, item) => acc + (item.price || 0), 0);
  const discountAmount = (totalAmount * discount) / 100;
  const grandTotal = totalAmount - discountAmount;

  const handleApplyCoupon = async () => {
    const code = form.couponCode.trim().toUpperCase();

    if (!code) {
      setErrors({ couponCode: "Please enter a coupon code" });
      return;
    }

    if (!/^[A-Z0-9]{3,15}$/.test(code)) {
      setErrors({ couponCode: "Invalid coupon code format" });
      return;
    }

    if (!user?.userId) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    try {
      setLoading(true);

      // 1️⃣ Validate usage with existing API
      const validResp = await axios.get(`${API_BASE_URL}/checkdiscountcode`, {
        params: { discountCode: code, userId: user.userId },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      if (!validResp.data?.success) {
        setErrors({ couponCode: "Invalid or already used coupon" });
        toast.error("Invalid coupon code");
        setLoading(false);
        return;
      }

      // 2️⃣ Fetch discount rules
      const discountResp = await axios.get(`${API_BASE_URL}/fetchdiscounts`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      const rules = discountResp.data?.success || [];

      // 3️⃣ Find matching coupon
      const rule = rules.find(
        (r: any) => r.discountCode.toUpperCase() === code
      );

      if (!rule) {
        setErrors({ couponCode: "Coupon not found" });
        toast.error("Invalid coupon code");
        setLoading(false);
        return;
      }

      // 4️⃣ Must be active
      if (rule.isActive !== "YES") {
        setErrors({ couponCode: "Coupon is inactive" });
        toast.error("This coupon is not active.");
        setLoading(false);
        return;
      }

      const { discountType, discountValue, subQty } = rule;

      const total = cart.reduce((sum, item) => sum + (item.price || 0), 0);
      let computedDiscount = 0;

      // 5️⃣ Apply discount logic
      if (discountType === "PERCENT") {
        if (subQty > 0 && cart.length < subQty) {
          setErrors({
            couponCode: `Requires at least ${subQty} subjects to apply`,
          });
          toast.error(
            `Add ${subQty - cart.length} more subjects to use this code.`
          );
          setLoading(false);
          return;
        }

        computedDiscount = (total * discountValue) / 100;
      } else if (discountType === "FLAT") {
        computedDiscount = Math.min(discountValue, total);
      } else if (discountType === "BUY_GET") {
        /**
         * Example: BUY4GET1
         * subQty = 4
         * discountValue = 1 (free 1 subject)
         */

        if (cart.length < subQty) {
          setErrors({
            couponCode: `Requires at least ${subQty} subjects to apply`,
          });
          toast.error(
            `Add ${subQty - cart.length} more subjects to use this code.`
          );
          setLoading(false);
          return;
        }

        // sort by price
        const sorted = [...cart].sort((a, b) => a.price - b.price);

        // give lowest-priced subjects free
        const freeItems = discountValue;
        const freeAmount = sorted
          .slice(0, freeItems)
          .reduce((sum, item) => sum + item.price, 0);

        computedDiscount = freeAmount;
      } else {
        setErrors({ couponCode: "Invalid discount type" });
        toast.error("Invalid coupon type");
        setLoading(false);
        return;
      }

      // 6️⃣ Apply coupon
      const percentageEquivalent = (computedDiscount / total) * 100;

      setDiscount(percentageEquivalent);
      setAppliedCoupon(code);
      setErrors({ couponCode: "" });

      toast.success(`Coupon applied successfully!`);
    } catch (err) {
      console.error(err);
      setErrors({ couponCode: "Failed to validate coupon" });
      toast.error("Invalid coupon code");
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const verifyPayment = async () => {
    try {
      const storedAmounts = secureLocalStorage.getItem("orderAmounts");
      const amounts = storedAmounts ? JSON.parse(storedAmounts as string) : {};
      const storedSubs = secureLocalStorage.getItem("subscriptionIds");
      const subscriptionIds = storedSubs
        ? JSON.parse(storedSubs as string)
        : [];
      const razorpay_order_id =
        (secureLocalStorage.getItem("orderId") as string) || "";
      const razorpay_payment_id =
        (secureLocalStorage.getItem("razorpay_payment_id") as string) || "";
      const razorpay_signature =
        (secureLocalStorage.getItem("razorpay_signature") as string) || "";
      const currency =
        (secureLocalStorage.getItem("currency") as string) || "INR";

      const verifyPayload = {
        userId: user?.userId ?? "",
        subscriptionIds,
        amounts,
        currency,
        razorpay_order_id,
        razorpay_payment_id,
        razorpay_signature,
        discountCode: appliedCoupon || "",
        // discountPercent: discount,
        discount: discountAmount,
      };

      await axios.post(`${API_BASE_URL}/verifyorder`, verifyPayload, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      navigate(ROUTES.SUBJECT);
      toast.success("Payment verified successfully!");
      secureLocalStorage.removeItem("subjectCart");
      secureLocalStorage.removeItem("subscriptionIds");
      secureLocalStorage.removeItem("orderAmounts");
      secureLocalStorage.removeItem("orderId");
      secureLocalStorage.removeItem("razorpay_payment_id");
      secureLocalStorage.removeItem("razorpay_signature");
      secureLocalStorage.removeItem("razorpay_order_id");
      setCart([]);
      setShowCoupon(false);
      setDiscount(0);
      setAppliedCoupon(null);
      setForm({ couponCode: "" });
    } catch {
      toast.error("Payment verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);

    const res = await loadRazorpay();
    if (!res) {
      toast.error("Razorpay SDK failed to load.");
      setLoading(false);
      return;
    }

    if (!user?.userId || !token) {
      toast.error("Session expired. Please log in again.");
      setLoading(false);
      return;
    }

    let subscriptionIds: string[] = [];
    try {
      const storedSubs = secureLocalStorage.getItem("subscriptionIds");
      if (storedSubs) {
        subscriptionIds = JSON.parse(storedSubs as string);
      }
    } catch {
      subscriptionIds = [];
    }

    if (!subscriptionIds.length) {
      toast.error("No subscriptions found.");
      setLoading(false);
      return;
    }

    // apply discount per subject
    const amounts: Record<string, number> = {};
    cart.forEach((item) => {
      if (item.subscriptionId) {
        const originalPrice = item.price || 0;
        const finalPrice =
          appliedCoupon && discount > 0
            ? Number((originalPrice * (1 - discount / 100)).toFixed(2))
            : originalPrice;
        amounts[item.subscriptionId] = finalPrice;
      }
    });

    const payload = {
      userId: user.userId,
      subscriptionIds,
      amounts,
      currency: "INR",
      receipt: `receipt_${user.userId}_${Date.now()}`,
      coupon: appliedCoupon || "",
      totalAmount: grandTotal, // rupees
      discountPercent: discount,
    };

    try {
      const { data: order } = await axios.post(
        `${API_BASE_URL}/createorder`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      secureLocalStorage.setItem("orderAmounts", JSON.stringify(amounts));
      secureLocalStorage.setItem("orderId", order.data.id);
      secureLocalStorage.setItem("currency", order.data.currency || "INR");

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount, // backend should use totalAmount * 100
        currency: "INR",
        name: "LLB360",
        description: "Order Payment",
        order_id: order.data.id,
        image: logo,
        handler: async (response: any) => {
          secureLocalStorage.setItem(
            "razorpay_payment_id",
            response.razorpay_payment_id
          );
          secureLocalStorage.setItem(
            "razorpay_signature",
            response.razorpay_signature
          );
          await verifyPayment();
        },
        prefill: {
          name: user?.fullName || "",
          email: user?.emailId || "",
          contact: user?.phoneNumber || "",
        },
        modal: {
          ondismiss: () => {
            toast.error("Payment cancelled.");
            setLoading(false);
          },
          confirm_close: true,
        },
        theme: { color: "#1E293B" },
      };

      const paymentObject = new (window as any).Razorpay(options);
      paymentObject.open();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Payment failed. Try again."
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100%-theme(space.2))] bg-myLight pb-4">
      <section className="border-b border-myInputBorder/60 bg-transparent backdrop-blur-md w-full sticky z-30 top-0 p-4">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="md:text-2xl sm:text-xl text-lg font-bold text-myLabelText capitalize">
            Checkout Details
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto mt-4 px-4">
        <div className="flex lg:flex-row flex-col gap-4">
          <section className="w-full">
            <section className="subjectTableSection">
              <div className="overflow-x-auto">
                <div className="max-h-[calc(100vh-17.75rem)] overflow-y-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="tableHeadRow">
                        <th className="tableHeadValue">Subject Name</th>
                        <th className="tableHeadValue">Amount (₹)</th>
                        <th className="tableHeadValue">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="text-center py-4 text-gray-500"
                          >
                            No items in cart.
                          </td>
                        </tr>
                      ) : (
                        cart.map((subject, index) => (
                          <tr key={index} className="tableBodyRow">
                            <td className="tableBodyValue whitespace-break-spaces">
                              {subject.subjectName}
                            </td>
                            <td className="tableBodyValue whitespace-break-spaces">
                              ₹ {subject.price.toFixed(2)}
                            </td>
                            <td className="px-6 py-3">
                              <button
                                onClick={() =>
                                  removeFromCart(subject.subjectName)
                                }
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                aria-label={`Remove ${subject.subjectName}`}
                                disabled={loading}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </section>

          <section className="border border-myInputBorder min-w-80 p-4 h-full rounded-lg bg-white">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Total Subjects</h2>
              <h3 className="text-base font-bold">{cart.length}</h3>
            </div>

            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Current Total</h2>
              <h3 className="text-base font-bold">
                ₹ {totalAmount.toFixed(2)}
              </h3>
            </div>

            <div className="flex flex-col w-full mt-4">
              {!showCoupon ? (
                <button
                  onClick={() => setShowCoupon(true)}
                  className="forgotPasswordLink text-xs cursor-pointer text-myLabelText hover:text-myButtonBg transition"
                >
                  Have a coupon code?
                </button>
              ) : (
                <div className="w-full flex flex-col gap-2">
                  <TextInput
                    id="couponCode"
                    name="couponCode"
                    type="text"
                    placeholder="Enter coupon code"
                    value={form.couponCode}
                    onChange={handleChange}
                    errorMessage={errors.couponCode}
                    icon={LuTicketPercent}
                    newClassName="w-full uppercase"
                    aria-label="Coupon Code"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    className="bg-myButtonBg cursor-pointer text-white text-sm py-2 rounded-lg hover:opacity-90 transition"
                    disabled={loading}
                  >
                    Apply Coupon
                  </button>
                </div>
              )}
            </div>

            {appliedCoupon && discount > 0 && (
              <div className="mt-4 border-t pt-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span>Coupon Applied ({appliedCoupon})</span>
                  <span className="text-green-600 font-semibold">
                    -{discount}%
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Discount Amount</span>
                  <span className="text-green-600 font-semibold">
                    ₹ {discountAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-between mb-1 text-red-500 text-sm pt-1.5">
              <span>Note: All the amounts are inclusive of GST.</span>
            </div>
            <div className="border-t mt-3 pt-3 flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold">Grand Total</h2>
              <h3 className="text-lg font-bold text-myButtonBg">
                ₹ {grandTotal.toFixed(2)}
              </h3>
            </div>

            <button
              onClick={handlePayment}
              className="bg-myButtonBg w-full cursor-pointer text-white text-sm py-2 rounded-lg hover:opacity-90 transition"
              disabled={loading || cart.length === 0}
              aria-label="Pay Now"
            >
              {loading ? "Processing..." : "Pay Now"}
            </button>
          </section>
        </div>
      </section>
    </div>
  );
};

export default CheckOutPage;
